import { useNavigate, useParams } from "@tanstack/react-router"
import { type Locale, useT } from "@holigral/shared/i18n"
import { LocaleSwitcher, useLocale } from "@holigral/shared/routing"
import { useUpdateLocaleMutation } from "./api"
import { useAuth } from "@/auth"

type Props = {
  /** Trigger button variant; defaults to a quiet ghost button. */
  variant?: "ghost" | "outline"
  align?: "start" | "end"
}

/**
 * Switches the UI language. On public pages the locale lives in the URL, so we
 * navigate to the same route under the new locale (the $locale route applies
 * it). In the app there is no URL locale, so we change it in place. Either way,
 * signed-in users persist the choice via PATCH /users/me/locale so it follows
 * them across devices. Lives in a feature (not shared) because it needs both
 * auth state and the profile mutation.
 */
export default function LanguageSwitcher({
  variant = "ghost",
  align = "end",
}: Props) {
  const { i18n } = useT()
  const { isAuthenticated } = useAuth()
  const [updateLocale] = useUpdateLocaleMutation()
  const navigate = useNavigate()
  const current = useLocale()
  // Public pages carry the locale in the URL ($locale param); the app does not.
  const onPublicPage = typeof useParams({ strict: false }).locale === "string"

  const select = (locale: Locale) => {
    if (isAuthenticated) {
      void updateLocale({ locale })
    }

    if (onPublicPage) {
      void navigate({
        to: ".",
        params: (prev) => ({ ...prev, locale }),
        replace: true,
      })
    } else {
      void i18n.changeLanguage(locale)
    }
  }

  return (
    <LocaleSwitcher
      current={current}
      onSelect={select}
      variant={variant}
      align={align}
    />
  )
}
